import { useCallback, useEffect, useState } from 'react';
import { clientScopedFetch } from '../lib/authedFetch';
import { useViewingAs } from '../hooks/useViewingAs';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

// Google's limit is 4096 BYTES (gbp-reviews.js REPLY_MAX_BYTES); an accented
// letter or emoji takes more than one, so the counter counts bytes too.
const REPLY_MAX_BYTES = 4096;
// UTF-8 byte count by code point — no TextEncoder, which the test DOM lacks.
const byteLength = (s) => {
  let n = 0;
  for (const ch of s) {
    const c = ch.codePointAt(0);
    n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
  }
  return n;
};

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.ok === false) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.code = body.code;
    throw err;
  }
  return body;
}

function Stars({ rating }) {
  if (!rating) return null;
  return (
    <span aria-label={`${rating} out of 5 stars`} title={`${rating} out of 5`} style={{ color:'#F5A623', letterSpacing:1, fontSize:14 }}>
      {'★'.repeat(rating)}<span style={{ color:'var(--border)' }}>{'★'.repeat(5 - rating)}</span>
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year:'numeric', month:'short', day:'numeric' }); }
  catch { return ''; }
}

// Google's reply moderation states, in words.
function ReplyStatus({ reply }) {
  if (reply.state === 'PENDING') {
    return <span className="cal-pill" style={{ color:'#f5a623', borderColor:'#f5a623' }} title="Google checks replies before they show publicly">Waiting for Google</span>;
  }
  if (reply.state === 'REJECTED') {
    return <span className="cal-pill" style={{ color:'#FF5252', borderColor:'#FF5252' }}>Rejected by Google</span>;
  }
  if (reply.state === 'APPROVED') {
    return <span className="cal-pill" style={{ color:'#31DCBA', borderColor:'#31DCBA' }}>Live</span>;
  }
  // No state: an older reply from before Google moderated replies.
  return <span className="cal-pill" style={{ color:'var(--text-muted)', borderColor:'var(--border)' }}>Posted</span>;
}

function ReviewMedia({ media }) {
  if (!media.length) return null;
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:10 }} data-testid="review-media">
      {media.map((m, i) => (
        <a
          key={i}
          href={m.video_url || m.thumbnail_url}
          target="_blank" rel="noopener noreferrer"
          title={m.label || (m.kind === 'video' ? 'Customer video' : 'Customer photo')}
          style={{ position:'relative', display:'block', width:84, height:84, borderRadius:8, overflow:'hidden', background:'#000', border:'1px solid var(--border)' }}
        >
          {m.thumbnail_url && <img src={m.thumbnail_url} alt={m.label || (m.kind === 'video' ? 'Customer video' : 'Customer photo')} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />}
          {m.kind === 'video' && (
            <span aria-hidden="true" style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:22, background:'rgba(0,0,0,0.35)' }}>▶</span>
          )}
        </a>
      ))}
    </div>
  );
}

function ReplyEditor({ initial, busy, error, onSave, onCancel }) {
  const [text, setText] = useState(initial || '');
  const bytes = byteLength(text.trim());
  const over = bytes > REPLY_MAX_BYTES;
  return (
    <div style={{ marginTop:10 }}>
      <textarea
        aria-label="Your reply"
        className="chip-modal-textarea"
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Thank them, answer their question, or put things right…"
        style={{ width:'100%', minHeight:90, boxSizing:'border-box' }}
      />
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, flexWrap:'wrap' }}>
        <button className="cal-post-btn cal-post-btn-danger" style={{ background:'#31DCBA', color:'#0d0f14', borderColor:'#31DCBA' }}
          disabled={busy || !text.trim() || over} onClick={() => onSave(text)}>
          {busy ? 'Posting…' : 'Post reply'}
        </button>
        <button className="cal-post-btn cal-post-btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
        <span style={{ fontSize:11, color: over ? '#FF5252' : 'var(--text-muted)', marginLeft:'auto' }}>
          {over ? 'Too long for Google' : bytes > REPLY_MAX_BYTES * 0.8 ? `${REPLY_MAX_BYTES - bytes} left` : ''}
        </span>
      </div>
      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4, lineHeight:1.5 }}>
        Replies are public on Google. Google checks them before they show, which can take a little while.
      </div>
      {error && <div className="cq-error" style={{ marginTop:6 }}>{error}</div>}
    </div>
  );
}

// The Reviews screen: the client's Google reviews, newest first, with
// replies and their moderation status.
//
// Reads live from Google through the backend on every load — nothing about a
// review is stored here. Every request goes through clientScopedFetch and the
// load effect keys on viewingAsClientId as well as clientId, for the same
// Phase 3 reason as every other panel: clientId is the signed-in user and
// does not change when an admin switches who they are viewing.
export default function ReviewsPanel({ clientId, onUnreadChange }) {
  const { viewingAsClientId } = useViewingAs();
  const [reviews, setReviews]     = useState(null);   // null = loading
  const [summary, setSummary]     = useState({ averageRating: null, totalReviewCount: null });
  const [nextPage, setNextPage]   = useState(null);
  const [loadError, setLoadError] = useState(null);   // { message, reconnect }
  const [loadingMore, setLoadingMore] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId]       = useState(null);
  const [rowError, setRowError]   = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [filter, setFilter]       = useState('all');  // all | unreplied
  // Reviews that were unread when the screen opened — shown with a "New" tag.
  const [newIds, setNewIds]       = useState(() => new Set());

  const fetchPage = useCallback(async (pageToken) => {
    const qs = pageToken ? `?pageToken=${encodeURIComponent(pageToken)}` : '';
    return readJson(await clientScopedFetch(`${API_BASE}/api/google-business/reviews${qs}`));
  }, []);

  const load = useCallback(async () => {
    setReviews(null);
    setLoadError(null);
    setEditingId(null);
    try {
      const d = await fetchPage(null);
      setReviews(d.reviews || []);
      setSummary({ averageRating: d.averageRating, totalReviewCount: d.totalReviewCount });
      setNextPage(d.nextPageToken || null);
      // Opening the screen is what "read" means. Only after the reviews have
      // actually loaded, so a failed load doesn't clear the badge. The server
      // leaves the badge alone when an admin is viewing a client, and says
      // how many are left either way.
      try {
        const m = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/reviews/mark-read`, { method: 'POST' }));
        setNewIds(new Set(m.newIds || []));
        if (onUnreadChange && typeof m.unread === 'number') onUnreadChange(m.unread);
      } catch { /* the badge stays as it was; nothing to show */ }
    } catch (err) {
      setReviews([]);
      setLoadError({ message: err.message, reconnect: err.code === 'gbp_reconnect', comingSoon: err.code === 'gbp_not_available' });
    }
  }, [fetchPage, onUnreadChange]);

  useEffect(() => {
    if (!clientId) return;
    load();
  }, [clientId, viewingAsClientId, load]);

  const loadMore = async () => {
    if (!nextPage || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await fetchPage(nextPage);
      setReviews(prev => [...(prev || []), ...(d.reviews || [])]);
      setNextPage(d.nextPageToken || null);
    } catch (err) {
      setLoadError({ message: err.message, reconnect: err.code === 'gbp_reconnect' });
    } finally {
      setLoadingMore(false);
    }
  };

  const setReply = (id, reply) => setReviews(prev => prev.map(r => (r.id === id ? { ...r, reply } : r)));
  const setErr = (id, msg) => setRowError(prev => ({ ...prev, [id]: msg }));

  const saveReply = async (review, text) => {
    setBusyId(review.id);
    setErr(review.id, '');
    try {
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/reviews/${encodeURIComponent(review.id)}/reply`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: text }),
      }));
      setReply(review.id, d.reply);
      setEditingId(null);
    } catch (err) {
      setErr(review.id, err.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteReply = async (review) => {
    setBusyId(review.id);
    setErr(review.id, '');
    try {
      await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/reviews/${encodeURIComponent(review.id)}/reply`, { method: 'DELETE' }));
      setReply(review.id, null);
    } catch (err) {
      setErr(review.id, err.message);
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  };

  const shown = (reviews || []).filter(r => filter === 'all' || !r.reply);
  const unrepliedCount = (reviews || []).filter(r => !r.reply).length;

  return (
    <div style={{ padding:32, maxWidth:820 }} data-testid="reviews-panel">
      <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap', marginBottom:16 }}>
        {summary.averageRating != null && (
          <div>
            <div style={{ fontSize:28, fontWeight:700, color:'var(--text-primary)', lineHeight:1 }}>{summary.averageRating.toFixed(1)}</div>
            <Stars rating={Math.round(summary.averageRating)} />
          </div>
        )}
        {summary.totalReviewCount != null && (
          <div style={{ fontSize:13, color:'var(--text-secondary)' }}>{summary.totalReviewCount} Google review{summary.totalReviewCount === 1 ? '' : 's'}</div>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button className={`chip-modal-pill${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>All</button>
          <button className={`chip-modal-pill${filter === 'unreplied' ? ' active' : ''}`} onClick={() => setFilter('unreplied')}>
            Needs a reply{reviews && unrepliedCount ? ` (${unrepliedCount}${nextPage ? '+' : ''})` : ''}
          </button>
          <button className="cal-post-btn cal-post-btn-ghost" onClick={load} title="Reload from Google">⟳</button>
        </div>
      </div>

      {reviews === null && (
        <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:'var(--text-muted)' }}>
          <div className="ig-spinner" /> Loading your Google reviews…
        </div>
      )}

      {loadError && (
        <div className="cq-error">
          {loadError.comingSoon
            ? 'Google Business Profile is coming soon — reviews will appear here once it’s available on your account.'
            : loadError.reconnect
              ? 'Google Business needs to be reconnected on the Connections page before reviews can load.'
              : `Couldn’t load reviews: ${loadError.message}`}
        </div>
      )}

      {reviews && !loadError && reviews.length === 0 && (
        <div style={{ fontSize:13, color:'var(--text-muted)' }}>No Google reviews yet.</div>
      )}
      {reviews && reviews.length > 0 && shown.length === 0 && (
        <div style={{ fontSize:13, color:'var(--text-muted)' }}>Every review here has a reply.</div>
      )}

      {shown.map(review => {
        const busy = busyId === review.id;
        const reply = review.reply;
        return (
          <div key={review.id} className="cal-day-panel" style={{ marginBottom:12 }} data-testid="review">
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {review.reviewer.photo_url
                ? <img src={review.reviewer.photo_url} alt="" referrerPolicy="no-referrer" style={{ width:32, height:32, borderRadius:'50%', objectFit:'cover' }} />
                : <div aria-hidden="true" style={{ width:32, height:32, borderRadius:'50%', background:'var(--accent-dim)', color:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13 }}>{(review.reviewer.name || '?').slice(0, 1).toUpperCase()}</div>}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }}>
                  {review.reviewer.name}
                  {newIds.has(review.id) && <span className="ap-badge" style={{ marginLeft:8 }}>New</span>}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <Stars rating={review.rating} />
                  <span style={{ fontSize:11, color:'var(--text-muted)' }}>{formatDate(review.created_at)}</span>
                </div>
              </div>
            </div>
            {review.comment
              ? <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:8, whiteSpace:'pre-wrap', lineHeight:1.5 }}>{review.comment}</div>
              : <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:8, fontStyle:'italic' }}>Left a rating without a comment.</div>}
            <ReviewMedia media={review.media} />

            {reply && editingId !== review.id && (
              <div style={{ marginTop:12, padding:'10px 12px', borderRadius:8, background:'rgba(255,255,255,0.03)', borderLeft:'3px solid var(--accent-border)' }} data-testid="reply">
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Your reply</span>
                  <ReplyStatus reply={reply} />
                  <span style={{ fontSize:11, color:'var(--text-muted)' }}>{formatDate(reply.updated_at)}</span>
                </div>
                <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:6, whiteSpace:'pre-wrap', lineHeight:1.5 }}>{reply.comment}</div>
                {reply.state === 'REJECTED' && (
                  <div style={{ fontSize:12, color:'#FF5252', marginTop:6, lineHeight:1.5 }} data-testid="reply-rejection">
                    Why: {reply.policy_violation_label} Edit the reply and post it again.
                  </div>
                )}
                {confirmDeleteId === review.id ? (
                  <span className="cal-post-confirm" style={{ marginTop:8, display:'inline-flex' }}>
                    <span className="cal-post-confirm-text">Delete this reply from Google?</span>
                    <button className="cal-post-btn cal-post-btn-danger" disabled={busy} onClick={() => deleteReply(review)}>{busy ? '…' : 'Yes'}</button>
                    <button className="cal-post-btn cal-post-btn-ghost" disabled={busy} onClick={() => setConfirmDeleteId(null)}>No</button>
                  </span>
                ) : (
                  <span className="cal-post-actions" style={{ marginTop:8, display:'inline-flex' }}>
                    <button className="cal-post-btn cal-post-btn-ghost" disabled={busy} onClick={() => setEditingId(review.id)}>✎ Edit</button>
                    <button className="cal-post-btn cal-post-btn-delete" disabled={busy} onClick={() => setConfirmDeleteId(review.id)} aria-label="Delete reply">✕</button>
                  </span>
                )}
              </div>
            )}

            {editingId === review.id ? (
              <ReplyEditor
                initial={reply ? reply.comment : ''}
                busy={busy}
                error={rowError[review.id]}
                onSave={text => saveReply(review, text)}
                onCancel={() => { setEditingId(null); setErr(review.id, ''); }}
              />
            ) : (
              <>
                {!reply && (
                  <button className="cal-post-btn cal-post-btn-ghost" style={{ marginTop:10 }} onClick={() => setEditingId(review.id)}>↩ Reply</button>
                )}
                {rowError[review.id] && <div className="cq-error" style={{ marginTop:6 }}>{rowError[review.id]}</div>}
              </>
            )}
          </div>
        );
      })}

      {nextPage && (
        <button className="cal-post-btn cal-post-btn-ghost" onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Load more reviews'}
        </button>
      )}
    </div>
  );
}
